<template>
  <div class="herstel-instructies rounded-sm">
    <button type="button" class="hi-toggle" :aria-expanded="open" @click="toggle">
      <i class="pi pi-question-circle hi-icon" />
      <span class="hi-title">Zo werkt het: herstel aanleveren</span>
      <i class="pi hi-chevron" :class="open ? 'pi-chevron-up' : 'pi-chevron-down'" />
    </button>

    <div v-if="open" class="hi-body">
      <p class="hi-intro">
        Er zijn constateringen die hersteld moeten worden. Volg deze stappen om de herstelverklaring in te dienen.
      </p>
      <ol class="hi-steps">
        <li v-if="variant === 'details'" class="hi-step">
          <span class="hi-step-number" />
          <span class="hi-step-text"
            >Klik op <strong>"Herstelpunten invullen"</strong> om naar de herstelpagina te gaan.</span
          >
        </li>
        <li class="hi-step">
          <span class="hi-step-number" />
          <span class="hi-step-text">
            Open bij elke constatering <strong>"Herstellen"</strong> en voeg minimaal één herstelfoto en een opmerking
            over het uitgevoerde herstel toe.
          </span>
        </li>
        <li class="hi-step">
          <span class="hi-step-number" />
          <span class="hi-step-text">
            Vul bij <strong>"Installateur Gegevens"</strong> de gegevens in van het bedrijf dat het herstel heeft
            uitgevoerd. Kies <strong>"Digitaal Formulier"</strong> (online invullen en ondertekenen) of
            <strong>"Handmatig Document"</strong> (downloaden, printen, ondertekenen en uploaden).
          </span>
        </li>
        <li class="hi-step">
          <span class="hi-step-number" />
          <span class="hi-step-text">
            Klik op <strong>"{{ submitLabel }}"</strong>. Uw wijzigingen worden daarbij automatisch opgeslagen.
          </span>
        </li>
        <li class="hi-step">
          <span class="hi-step-number" />
          <span class="hi-step-text">
            De inspecteur controleert uw aanlevering. U ontvangt bericht als een herstelpunt wordt afgekeurd of zodra
            alles is goedgekeurd.
          </span>
        </li>
      </ol>
      <p v-if="hasHerinspectie" class="hi-note">
        Let op: voor gemarkeerde constateringen volgt na het indienen nog een fysieke herinspectie door de inspecteur.
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref } from "vue";

  const props = withDefaults(
    defineProps<{
      /** "details" shows the extra "Herstelpunten invullen" step; "herstel" starts on the herstel page itself. */
      variant: "details" | "herstel";
      scopeLabel?: string;
      hasHerinspectie?: boolean;
    }>(),
    { scopeLabel: "", hasHerinspectie: false },
  );

  // One shared key: a user who collapsed the panel once keeps it collapsed on
  // both pages; without a stored choice the herstel page starts expanded.
  const STORAGE_KEY = "herstel-instructies-collapsed";

  const readStored = (): boolean | null => {
    try {
      const value = localStorage.getItem(STORAGE_KEY);
      return value === null ? null : value === "1";
    } catch {
      return null;
    }
  };

  const storedCollapsed = readStored();
  const open = ref(storedCollapsed === null ? props.variant === "herstel" : !storedCollapsed);

  const toggle = () => {
    open.value = !open.value;
    try {
      localStorage.setItem(STORAGE_KEY, open.value ? "0" : "1");
    } catch {
      // Storage unavailable (private mode) — the toggle still works for this visit.
    }
  };

  const submitLabel = computed(() =>
    props.scopeLabel ? `Herstelverklaring ${props.scopeLabel} indienen` : "Herstelverklaring indienen",
  );
</script>

<style scoped>
  .herstel-instructies {
    border: 1px solid var(--p-background-200);
    background: var(--p-background-0);
  }

  .hi-toggle {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    padding: 0.75rem 1rem;
    background: none;
    border: none;
    cursor: pointer;
    text-align: left;
    font: inherit;
    color: var(--p-background-800);
  }

  .hi-icon {
    color: var(--p-primary-500);
  }

  .hi-title {
    flex: 1;
    font-size: 0.95rem;
    font-weight: 600;
  }

  .hi-chevron {
    font-size: 0.85rem;
    color: var(--p-background-500);
  }

  .hi-body {
    padding: 0 1rem 1rem;
  }

  .hi-intro {
    margin: 0 0 0.75rem;
    font-size: 0.9rem;
    color: var(--p-background-700);
  }

  .hi-steps {
    counter-reset: hi-step;
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
  }

  .hi-step {
    counter-increment: hi-step;
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
  }

  .hi-step-number {
    width: 1.5rem;
    height: 1.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--p-primary-500);
    color: white;
    border-radius: 50%;
    font-size: 0.8rem;
    font-weight: 600;
    flex-shrink: 0;
    margin-top: 0.1rem;
  }

  .hi-step-number::before {
    content: counter(hi-step);
  }

  .hi-step-text {
    font-size: 0.9rem;
    line-height: 1.55;
    color: var(--p-background-700);
  }

  .hi-note {
    margin: 0.875rem 0 0;
    font-size: 0.875rem;
    color: var(--p-amber-700, #b45309);
  }

  /* Amber-700 fails contrast on the dark surface; lighten it in dark mode. */
  :global(.p-dark) .herstel-instructies .hi-note {
    color: var(--p-amber-400, #fbbf24);
  }
</style>
